import { ElevatorIcon, Escalator, GoStraightIcon, MoveToBuildingIcon, StairsIcon, TurnLeftIcon, TurnRightIcon } from '@/components/icons/route-simulation';
import { DEFAULT_ROUTE_SIMULATION_PARAMS, ROUTE_DIRECTION_TEXT, TRANSPORTATION_TEXT } from '@/constants/floor-guide';
import { MAP_ICON_URL } from '@/constants/icons';
import { EASE_TYPE } from '@/enums/floor-guide';
import type { Direction } from '@/types/floor-guide';
import type { RoutePoint } from '@/types/route-simulation';
import { TRANSPORTATION_REQUEST_TYPE, type IRouteOption } from 'dabeeomaps';
import type { INaviAnimOption } from 'dabeeomaps/dist/src/model/map/INaviAnimOption';
import type { INaviList } from 'dabeeomaps/dist/src/model/map/INaviList';
import type { INaviOptions } from 'dabeeomaps/dist/src/model/map/INaviOptions';
import type { TFunction } from 'i18next';
import { isValidNumber } from './number';

export const generateGetRouteOptions = (startPoint: RoutePoint, endPoint: RoutePoint): IRouteOption => {
    return {
        origin: {
            floorId: startPoint.floorId,
            ...(isValidNumber(startPoint?.position?.x) && isValidNumber(startPoint?.position?.y)
                ? { position: { x: startPoint.position.x, y: startPoint.position.y } }
                : {}),
            ...(startPoint?.poiId ? { poiId: startPoint.poiId } : {}),
        },
        destination: {
            floorId: endPoint.floorId,
            ...(isValidNumber(endPoint?.position?.x) && isValidNumber(endPoint?.position?.y)
                ? { position: { x: endPoint.position.x, y: endPoint.position.y } }
                : {}),
            ...(endPoint?.poiId ? { poiId: endPoint.poiId } : {}),
        },
        type: [
            TRANSPORTATION_REQUEST_TYPE.RECOMMENDATION,
            TRANSPORTATION_REQUEST_TYPE.ELEVATOR,
            TRANSPORTATION_REQUEST_TYPE.ESCALATOR,
            TRANSPORTATION_REQUEST_TYPE.STAIRS,
        ],
    };
};

export const generateNavigationOptions = (
    solidLineWidth?: number | null,
    startIconSize: { width: number; height: number } = { width: 26, height: 26 },
    endIconSize: { width: number; height: number } = { width: 48, height: 68 },
    lineColor?: string | null,
    destinationIconUrl?: string | null
): Partial<INaviOptions> => {
    const lineWeight = solidLineWidth ?? DEFAULT_ROUTE_SIMULATION_PARAMS.LINE_WEIGHT;
    const routeLineColor = lineColor || '#2CC4CE';

    return {
        defaultLineOption: {
            lineColor: routeLineColor,
            solidLineEnabled: true,
            solidLineWidth: lineWeight,
        },

        origin: {
            markerOptions: {
                iconUrl: MAP_ICON_URL.START_POINT_ICON,
                width: startIconSize.width,
                height: startIconSize.height,
            },
        },
        destination: {
            markerOptions: {
                iconUrl: destinationIconUrl || MAP_ICON_URL.DESTINATION_POINT_ICON,
                width: endIconSize.width,
                height: endIconSize.height,
            },
            lineOptions: {
                solidLineWidth: lineWeight,
            },
        },
    };
};

export const generateWalkingOptions = (
    easeType?: string,
    speedRate?: number | null,
    zoom?: number,
    iconSize: { width: number; height: number } = { width: 66, height: 66 },
    iconUrl?: string | null
): INaviAnimOption | undefined => {
    const speed = speedRate ?? DEFAULT_ROUTE_SIMULATION_PARAMS.SPEED;
    const ease = (easeType ?? EASE_TYPE.LINEAR) as EASE_TYPE;
    return {
        zoom: zoom,
        markerOptions: {
            iconUrl: iconUrl || MAP_ICON_URL.WALKING_PERSON_ICON,
            width: iconSize.width,
            height: iconSize.height,
        },
        ease: ease,
        speedRate: speed,
        removeIcon: true,
    };
};

export const generateRouteDirectionIcon = (direction?: string) => {
    if (!direction) {
        return undefined;
    }

    switch (direction) {
        case ROUTE_DIRECTION_TEXT.GO_STRAIGHT:
            return <GoStraightIcon />;
        case ROUTE_DIRECTION_TEXT.TURN_RIGHT:
            return <TurnRightIcon />;
        case ROUTE_DIRECTION_TEXT.TURN_LEFT:
            return <TurnLeftIcon />;
        default:
            return undefined;
    }
};

export const generateTransportationIcon = (transportation?: string) => {
    if (!transportation) {
        return undefined;
    }

    switch (transportation) {
        case TRANSPORTATION_TEXT.TOW_WAYS_ESCALATOR:
        case TRANSPORTATION_TEXT.ESCALATOR_UP:
        case TRANSPORTATION_TEXT.ESCALATOR_DOWN:
            return <Escalator />;
        case TRANSPORTATION_TEXT.ELEVATOR:
            return <ElevatorIcon />;
        case TRANSPORTATION_TEXT.STAIRS:
            return <StairsIcon />;
        default:
            return undefined;
    }
};

export const generateRouteIcon = (direction?: string, transportation?: string) => {
    if (!direction && transportation !== TRANSPORTATION_TEXT.WALKING) {
        return generateTransportationIcon(transportation);
    }

    if (!direction && transportation === TRANSPORTATION_TEXT.WALKING) {
        return <MoveToBuildingIcon />;
    }

    return generateRouteDirectionIcon(direction);
};

export const generateDirectionText = (t: TFunction, direction: string) => {
    if (!direction) {
        return '';
    }

    switch (direction) {
        case ROUTE_DIRECTION_TEXT.GO_STRAIGHT:
            return t('floor_guide.direction.go_straight');
        case ROUTE_DIRECTION_TEXT.TURN_RIGHT:
            return t('floor_guide.direction.turn_right');
        case ROUTE_DIRECTION_TEXT.TURN_LEFT:
            return t('floor_guide.direction.turn_left');
        default:
            return '';
    }
};

export const generateTransportationText = (t: TFunction, transportation: string, floorName?: string) => {
    if (!transportation) {
        return '';
    }

    switch (transportation) {
        case TRANSPORTATION_TEXT.TOW_WAYS_ESCALATOR:
        case TRANSPORTATION_TEXT.ESCALATOR_UP:
        case TRANSPORTATION_TEXT.ESCALATOR_DOWN:
            return t('floor_guide.message.transport_move', {
                transportation: t('floor_guide.transportation.escalator'),
                floorName: floorName,
            });
        case TRANSPORTATION_TEXT.ELEVATOR:
            return t('floor_guide.message.transport_move', {
                transportation: t('floor_guide.transportation.elevator'),
                floorName: floorName,
            });
        case TRANSPORTATION_TEXT.STAIRS:
            return t('floor_guide.message.stairs_move', {
                floorName: floorName,
            });
        default:
            return '';
    }
};

export const formatDistance = (distance?: number) => {
    if (!isValidNumber(distance) || distance < 0) {
        return 0;
    }

    return Math.round(distance);
};

export const generateRouteDescription = (
    t: TFunction,
    navigationItem: INaviList,
    floorName: string,
    isLast: boolean = false,
    destinationPoiName?: string,
    buildingName?: string
) => {
    if (isLast && destinationPoiName) {
        return t('floor_guide.message.arrive', {
            poiName: destinationPoiName,
            floorName: floorName,
        });
    }

    if (navigationItem?.transportation === TRANSPORTATION_TEXT.WALKING) {
        if (!navigationItem?.direction) {
            return t('floor_guide.message.move_to_building');
        }

        return t('floor_guide.message.walk', {
            direction: generateDirectionText(t, navigationItem?.direction),
            distance: formatDistance(navigationItem?.distance),
        });
    }

    return generateTransportationText(t, navigationItem?.transportation, floorName);
};

export const generateDisplayDirections = (
    t: TFunction,
    generateFloorName: (floorId: string) => string,
    directions?: INaviList[],
    destinationPoiName?: string,
    generateBuildingName?: (buildingId?: string) => string | undefined
): Direction[] => {
    if (!Array.isArray(directions) || !directions?.length) {
        return [];
    }

    return directions.map((direction, index) => {
        const isLast = index === directions.length - 1;
        const nextDirection = directions[index + 1] ?? {};
        const isChangingFloor = direction?.floorId !== nextDirection?.floorId;
        const targetFloorId = isLast || !isChangingFloor ? direction?.floorId : nextDirection?.floorId;
        return {
            id: index.toString(),
            icon: generateRouteIcon(direction?.direction, direction?.transportation),
            description: generateRouteDescription(
                t,
                direction,
                generateFloorName(targetFloorId),
                isLast,
                destinationPoiName,
                generateBuildingName?.(nextDirection?.buildingId)
            ),
        };
    });
};
